// The Needs Action status comment — the pure parts.
//
// ── Why this exists ───────────────────────────────────────────────────────
// Needs Action could record that something was DONE and nothing else. An item
// nobody had touched and an item someone had looked at and deliberately parked
// rendered identically. With two people on the same queue that is duplicated
// effort at best and a silently stalled item at worst.
//
// status_comment is the parked-state channel — "CRC has eyes on but waiting
// for AGL feedback". It is deliberately NOT action_note:
//
//   action_note      what was DONE to close this. Written once, at the moment
//                    the item leaves the list, and copied into the
//                    handled_actions audit log.
//   status_comment   what the situation IS. Live for as long as the item is
//                    open, rewritten as often as the situation changes, and
//                    never an audit record of anything.
//
// That difference is why action_note is held in local React state until the
// handle button is pressed (App.js, actionNotes) and this one is not. Both are
// deliberate. A completion record has no reason to exist before the completion,
// so there is nothing for a pending row to load; a status comment is worthless
// unless the other operator can read it right now. Do not "fix" the first by
// making it look like the second.
//
// Neither is derivable from the other, which is why this is a new column and
// not a reuse — the same reasoning that gave dismissed_at its own column
// rather than overloading action_type. See src/dismissal.js.
//
// ── The invariant everything here protects ────────────────────────────────
// A comment has no effect on the queue. It cannot move an item into or out of
// Needs Action (that is action_type + dismissed_at) and it cannot change a
// dollar figure. No patch built in this file contains actionType, dismissedAt,
// or any amount key — and because entryToDb emits a column only when its key
// is present, that absence is the guarantee, not a convention.
//
// These live outside App.js so the field can be tested without rendering the
// app; nothing here touches Supabase.

/** How long a pause in typing counts as "done", in ms. Blur flushes early. */
export const COMMENT_DEBOUNCE_MS = 800

/**
 * The stored form of a comment: trimmed, or null when there is nothing to say.
 *
 * Whitespace-only collapses to null rather than to '   ' so that a comment
 * cleared by selecting-all-and-deleting is genuinely gone — otherwise the row
 * would keep its indicator forever on a comment with no text in it.
 */
export function normalizeComment(text) {
  if (typeof text !== 'string') return null
  const trimmed = text.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Does this entry carry a comment?
 *
 * The list indicator's predicate. Reads the STORED value, never a draft in
 * progress — the chip means "there is a saved comment here", which is also
 * what makes it double as confirmation that the save landed.
 */
export function hasStatusComment(entry) {
  return !!entry && normalizeComment(entry.statusComment) != null
}

/**
 * The write.
 *
 * All three keys are always present, including when clearing. That is
 * load-bearing for the same reason undismissPatch spells out dismissedAt:
 * null — entryToDb keys off presence, so an omitted key leaves the old value
 * in the row and "delete my comment" would appear to do nothing.
 *
 * Clearing nulls the byline too. A timestamp and an author with no text is a
 * row that claims someone commented and shows nothing, which is worse than
 * either having both or neither.
 */
export function statusCommentPatch(text, nowIso, byEmail) {
  const body = normalizeComment(text)
  if (body == null) {
    return { statusComment: null, statusCommentAt: null, statusCommentBy: null }
  }
  return {
    statusComment: body,
    statusCommentAt: nowIso,
    statusCommentBy: byEmail || null,
  }
}

/**
 * What the textarea shows.
 *
 * A draft wins over the stored value whenever one exists — including the empty
 * string, which is a real edit (the operator clearing the field) and not an
 * absence. `undefined` is the only "no draft" signal, which is why callers must
 * delete the key rather than set it to ''.
 *
 * This fallback is what lets the refetch-on-focus fix work at all: a refetch
 * replaces every row in the list, and rows the operator is not editing pick up
 * the other user's comment immediately, while a row mid-edit keeps its draft.
 */
export function resolveCommentValue(entry, draft) {
  if (draft !== undefined) return draft
  return (entry && entry.statusComment) || ''
}

/**
 * Is this draft worth a round trip?
 *
 * There is no Save button, so blur fires a save attempt on every field the
 * operator so much as tabs through. Comparing normalized forms means an
 * untouched field, and a field that gained only whitespace, cost nothing.
 */
export function isCommentDirty(entry, draft) {
  if (draft === undefined) return false
  return normalizeComment(draft) !== normalizeComment(entry && entry.statusComment)
}

/**
 * The byline name — local-part of the email, matching how the Completed /
 * handled drawer already renders handled_by.
 */
export function commentAuthorLabel(email) {
  if (!email) return 'someone'
  return String(email).split('@')[0]
}
